'use client'

import { DatoLinkFragment } from '@/components/DatoLink/DatoLink.generated'
import {
	InitView,
	NoResultsView,
	ResultsView,
} from '@/components/Search/SearchViews/index'
import { LanguageRecord } from '@/graphql/types'
import { useSearchForm } from '@/hooks/useSearchForm'
import { localeToDatoLocale } from '@/lib/locale.utils'
import { Button, Loader } from '@desoutter/uikit'
import { Search } from '@desoutter/uikit/src/components/icons'
import { cn } from '@desoutter/uikit/src/utils'
import { Maximize2 } from 'lucide-react'
import { useParams } from 'next/navigation'
import { LocalizedLink } from '../LocalizedLink'
import SearchInput from '../Search/SearchInput'

export function HeaderSearchBar({
	className,
	translations,
	contactPageLink,
}: {
	className?: string
	translations: LanguageRecord
	contactPageLink: DatoLinkFragment
}) {
	const { lang } = useParams()
	const datoLang = localeToDatoLocale(lang as string)

	const {
		isFocused,
		setFocused,
		localQuery,
		setLocalQuery,
		debouncedQuery,
		isResult,
		isLoading,
		data,
		products,
		resources,
		dt,
		searchHistory,
		handleSearchHistoryClick,
		input,
	} = useSearchForm({
		syncWithUrl: false,
		initializeFromUrl: false,
		lang: lang as string,
		instanceId: 'header-search',
	})

	function close() {
		setFocused(false)
		setLocalQuery('')
	}

	const handleRedirect = () => {
		setFocused(false)
		// Clear localQuery to avoid interference with the search page
		setLocalQuery('')
	}

	return (
		<div
			id="search-bar"
			className={cn('flex justify-end items-center h-12 w-full', className)}
		>
			<button
				className="relative w-6 h-6 rounded-full opacity-100 flex items-center justify-center z-10"
				title="search"
				onClick={() => setFocused(true)}
			>
				<Search className="stroke-gray-900 dark:stroke-white" />
			</button>

			{isFocused && (
				<div
					className="fixed top-0 left-0 w-screen h-screen bg-black/50 pt-48 z-40 px-0 mx-0 flex flex-col items-center justify-start"
					onClick={() => setFocused(false)}
				>
					<div
						className="relative bg-white rounded-3xl p-2 w-[90vw] lg:w-[50vw] max-w-[768px] max-h-[80vh] text-gray-900 flex flex-col h-auto flex-grow-0"
						style={{
							boxShadow: '0px 12px 24px 0px rgba(0, 0, 0, 0.08)',
						}}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex gap-2 items-center">
							{/* Search input */}
							<SearchInput
								input={input}
								localQuery={localQuery}
								setLocalQuery={setLocalQuery}
								translations={translations}
								className="flex-grow py-3 pl-4"
								isModal
							/>
							{/* Expand button */}
							<LocalizedLink
								href={`/search?q=${localQuery}`}
								locale={datoLang}
								onClick={handleRedirect}
							>
								<Button
									className="min-w-12 min-h-12 px-0 py-0 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0 text-white"
									noIcon
								>
									<Maximize2 className="w-6 h-6 min-w-4 min-h-4 text-white" />
								</Button>
							</LocalizedLink>
						</div>
						<div className="flex-1 min-h-0 overflow-hidden pt-2 flex flex-col">
							{(!localQuery || localQuery.length < 3) && (
								<InitView
									text={translations.searchBoxText}
									searchHistory={searchHistory}
									onSearchClick={handleSearchHistoryClick}
									translations={translations}
									isModal
								/>
							)}
							{isLoading && (
								<div className="flex items-center justify-center h-full">
									<Loader />
								</div>
							)}
							{!isLoading && debouncedQuery.length > 2 && !isResult && (
								<NoResultsView
									query={debouncedQuery}
									text={translations.noResult}
									noResultContact={translations.noResultContact}
									contactPageLink={contactPageLink}
									onClose={close}
									searchHistory={searchHistory}
									onSearchClick={handleSearchHistoryClick}
									translations={translations}
								/>
							)}
							{!isLoading && debouncedQuery.length > 2 && !!isResult && (
								<ResultsView
									data={data}
									products={products}
									dt={dt}
									resources={resources}
									count={isResult}
									translations={translations}
									isModal
								/>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
